import { ActivatedRoute, Router } from '@angular/router';
import { ConfigStateService, LocalizationService, Rest, SubscriptionService } from '@abp/ng.core';
import { FormBuilder, Validators } from '@angular/forms';
import { ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ClassificationTypeService, LabelSubContext } from '@digiwall/goliath-core/proxy';
import { AfterViewInit, Directive, Injector, ViewChild, signal } from '@angular/core';
import {
  AddressDto,
  BaseCustomerBooleanDto,
  CustomerService,
  PointOfSaleLookupDto,
  WorkingSiteLookUpDto,
} from '@digiwall/goliath-contacts/proxy';
import { AddressRole } from '@digiwall/goliath-contacts';
import {
  DayBookService,
  CustomerDocDto,
  CreateUpdateCustomerDocDto,
  CustomerDocDefaultValueDto,
} from '@digiwall/goliath-documents/proxy';
import { DocTemplateService } from '../base-docs/detail/doc-template-selection/doc-template.service';
import { DeactivatableComponentService } from '@digiwall/core';
import {
  BaseThirdPartyDocDetail,
  IThirdPartyDocService,
  IThirdPartyService,
} from '../base-third-party-docs/base-third-party-doc-detail';
import { distinctUntilChanged, first, mergeMap, Observable, of, takeUntil } from 'rxjs';
import { historySidePanelTrigger, SidePanelComponent } from '@digiwall/ui-kit';
import { DocSaveRequestContext } from '../base-docs/detail/doc-save-request-context';
import { ITemplateToPrintService, LabelPrinterHelper } from '@digiwall/goliath-products';

export interface ISalesThirdPartyService extends IThirdPartyService {
  getEntityAddress: (entityId: string, config?: Partial<Rest.Config>) => Observable<string>;
  getBooleanInformations: (
    customerId: string,
    config?: Partial<Rest.Config>,
  ) => Observable<BaseCustomerBooleanDto>;
}

export interface ICustomerDocService<
  TDocDto extends CustomerDocDto,
  TCreateUpdateDto extends CreateUpdateCustomerDocDto,
  TDefaultValueDto extends CustomerDocDefaultValueDto,
> extends IThirdPartyDocService<TDocDto, TCreateUpdateDto, TDefaultValueDto>,
    ITemplateToPrintService {
  updateCustomerProjectId: (
    docId: string,
    customerProjectId: string,
    concurrencyStamp: string,
  ) => Observable<TDocDto>;
  isDocumentDateValid: (documentDate: string, number: number) => Observable<boolean>;
}
@Directive()
export abstract class BaseCustomerDocDetail<
    TDocService extends ICustomerDocService<TDocDto, TCreateUpdateDto, TDefaultValueDto>,
    TDocDto extends CustomerDocDto,
    TCreateUpdateDto extends CreateUpdateCustomerDocDto,
    TDefaultValueDto extends CustomerDocDefaultValueDto,
  >
  extends BaseThirdPartyDocDetail<
    TDocService,
    TDocDto,
    TCreateUpdateDto,
    TDefaultValueDto,
    ISalesThirdPartyService
  >
  implements AfterViewInit
{
  @ViewChild('history') historyPanel: SidePanelComponent;

  protected override thirdPartyService: ISalesThirdPartyService;
  protected readonly customerProjectId = signal<string>(null);

  constructor(
    route: ActivatedRoute,
    router: Router,
    subscriptionService: SubscriptionService,
    docService: TDocService,
    formBuilder: FormBuilder,
    toastService: ToasterService,
    breadcrumbService: BreadcrumbService,
    dayBookService: DayBookService,
    localizationService: LocalizationService,
    classificationTypeService: ClassificationTypeService,
    quoteTemplateService: DocTemplateService,
    injector: Injector,
    customerService: CustomerService,
    config: ConfigStateService,
    confirmationService: ConfirmationService,
    deactivableComponentService: DeactivatableComponentService,
    protected labelPrinterHelper: LabelPrinterHelper,
  ) {
    super(
      route,
      router,
      subscriptionService,
      docService,
      formBuilder,
      toastService,
      breadcrumbService,
      dayBookService,
      localizationService,
      classificationTypeService,
      quoteTemplateService,
      injector,
      config,
      confirmationService,
      deactivableComponentService,
      customerService,
    );
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
    this.triggers.set([...this.triggers(), historySidePanelTrigger(this.historyPanel)]);
  }
  override get formModel() {
    let formModel = super.formModel;

    formModel = Object.assign({}, super.formModel, {
      discount: [null, [Validators.min(0), Validators.max(99.99)]],
      allowPartialDelivery: [false],
      deliveryModeId: [null],
      representativeCommision: [null],
      estimatedAvailability: [null],
      representativeId: [null],
      pointOfSaleId: [null],
      pointOfSale: [''],
      workingSiteId: [null],
      deliveryAddress: this.formBuilder.group({
        thirdPartyAddressId: [null],
        street: [null],
        name: [null],
        localityId: [null],
        countryId: [null],
        latitude: [null],
        longitude: [null],
        address2: [null],
        contactInfo: [null],
      }),
      invoicingAddress_ThirdPartyAddressId: [null],
      customerProjectId: [null],
    });
    return formModel;
  }

  protected override initFormObservers() {
    super.initFormObservers();

    this.subscriptionService.addOne(
      this.form.get('deliveryAddress').get('name').valueChanges,
      (addressSelected: any) => {
        if (addressSelected) {
          let addresses = this.thirdPartyDeliveryAddresses$.value;

          const name =
            typeof addressSelected == String.name.toLowerCase()
              ? addressSelected.toLowerCase()
              : addressSelected.name.toLowerCase();
          let address = addresses.find(x => x.name.toLowerCase() == name);
          this.thirdPartyDeliveryAddress.set(address);

          if (address == null) return;

          this.applyDeliveryAddressFields(address);
        } else {
          this.thirdPartyDeliveryAddress.set(null);
          this.clearDeliveryAddressFields();
        }
      },
    );

    this.subscriptionService.addOne(
      this.form.get('deliveryAddress').get('street').valueChanges,
      () => {
        this.setThirdPartyDeliveryAddress('street');
      },
    );

    this.subscriptionService.addOne(
      this.form.get('deliveryAddress').get('localityId').valueChanges,
      () => {
        this.setThirdPartyDeliveryAddress('localityId');
      },
    );

    this.subscriptionService.addOne(
      this.form.get('deliveryAddress').get('countryId').valueChanges,
      () => {
        this.setThirdPartyDeliveryAddress('countryId');
      },
    );

    if (this.form.get('invoicingAddress_ThirdPartyAddressId')) {
      this.subscriptionService.addOne(
        this.form.get('invoicingAddress_ThirdPartyAddressId').valueChanges,
        (addressId: string) => {
          if (addressId) {
            let addresses = this.thirdPartyInvoicingAddress$.value;

            let address = addresses.find(x => x.id.toLowerCase() == addressId.toLowerCase());
            if (address == null) return;

            this.form.get('invoicingAddress_Address1').setValue(address.street);
            this.form.get('invoicingAddress_Address2').setValue(address.address2);
            this.form.get('invoicingAddress_CountryId').setValue(address.countryId);
            this.form.get('invoicingAddress_LocalityId').setValue(address.localityId);
          }
        },
      );
    }

    this.subscriptionService.addOne(
      this.form.get('customerProjectId').valueChanges,
      (projectId: string) => this.customerProjectId.set(projectId),
    );
  }

  protected printLabel() {
    this.labelPrinterHelper.print(
      this.docService,
      this.entity.id,
      LabelSubContext.CustomerDocOrderInfo,
    );
  }

  openLabelDesigner() {
    this.formStateService.checkDirtyForm(this.form, () =>
      this.labelPrinterHelper.selectLayout(this.entity.id, LabelSubContext.CustomerDocOrderInfo),
    );
  }

  protected override beforeSave(): Observable<DocSaveRequestContext> {
    return super.beforeSave().pipe(
      mergeMap(context => {
        const customerProjectIdControl = this.form.get('customerProjectId');
        const customerProjectId = customerProjectIdControl?.value;
        const currentCustomerProjectId = (this.entity as any)?.customerProjectId ?? null;

        if (
          this.isFormDisabled() &&
          this.entity?.id != null &&
          customerProjectIdControl?.touched &&
          customerProjectId !== currentCustomerProjectId
        ) {
          return this.docService
            .updateCustomerProjectId(
              this.entity.id,
              customerProjectId,
              this.entity.concurrencyStamp,
            )
            .pipe(
              mergeMap(response => {
                this.form.get('concurrencyStamp').setValue(response.concurrencyStamp);
                this.entity.concurrencyStamp = response.concurrencyStamp;
                (this.entity as any).customerProjectId = customerProjectId;
                customerProjectIdControl.markAsUntouched();

                return of(context);
              }),
            );
        }
        return of(context ?? ({} as DocSaveRequestContext));
      }),
    );
  }

  protected override onThirdPartyChanged(thirdPartyId: string): void {
    if (thirdPartyId != null && thirdPartyId != this.entity.thirdPartyId) {
      this.thirdPartyService
        .getThirdPartyWarningMessage(thirdPartyId)
        .subscribeOnce(thirdPartyWarningMessageDto => {
          if (thirdPartyWarningMessageDto != null) {
            this.confirmationService
              .warn(
                thirdPartyWarningMessageDto.warningMessage,
                this.localizationService.instant(
                  'Doc::CustomerHasWarningMessage',
                  thirdPartyWarningMessageDto.thirdPartyCode,
                ),
                { hideCancelBtn: true, yesText: this.localizationService.instant('::Ok') },
              )
              .subscribeOnce();
          }
        });

      this.thirdPartyService
        .getBooleanInformations(this.form.get('thirdPartyId').value)
        .subscribeOnce(response => {
          this.form.get('allowPartialDelivery').setValue(response.allowPartialDelivery);
          this.form.get('retainBackOrders').setValue(response.retainBackOrders);
        });
    }

    super.onThirdPartyChanged(thirdPartyId);
  }

  protected override onThirdPartyAddressesUpdated(addresses: AddressDto[]): void {
    const deliveryAddresses = addresses.filter(address =>
      address.roles.some(role => role == AddressRole.Delivery),
    );

    if (deliveryAddresses.length == 1) {
      this.thirdPartyDeliveryAddress.set(deliveryAddresses[0]);
      this.applyDeliveryAddressFields(deliveryAddresses[0]);
    } else {
      this.thirdPartyDeliveryAddress.set(null);
      this.clearDeliveryAddressFields();
    }
  }

  private applyDeliveryAddressFields(address: AddressDto): void {
    const deliveryAddressForm = this.form.get('deliveryAddress');
    deliveryAddressForm.get('name').setValue(address.name, { emitEvent: false });
    deliveryAddressForm.get('street').setValue(address.street, { emitEvent: false });
    deliveryAddressForm.get('address2').setValue(address.address2, { emitEvent: false });
    deliveryAddressForm.get('countryId').setValue(address.countryId, { emitEvent: false });
    deliveryAddressForm.get('localityId').setValue(address.localityId, { emitEvent: false });
    deliveryAddressForm.get('latitude').setValue(address.latitude, { emitEvent: false });
    deliveryAddressForm.get('longitude').setValue(address.longitude, { emitEvent: false });
  }

  private clearDeliveryAddressFields(): void {
    const deliveryAddressForm = this.form.get('deliveryAddress');
    deliveryAddressForm.get('name').setValue(null, { emitEvent: false });
    deliveryAddressForm.get('street').setValue(null, { emitEvent: false });
    deliveryAddressForm.get('address2').setValue(null, { emitEvent: false });
    deliveryAddressForm.get('countryId').setValue(null, { emitEvent: false });
    deliveryAddressForm.get('localityId').setValue(null, { emitEvent: false });
    deliveryAddressForm.get('latitude').setValue(null, { emitEvent: false });
    deliveryAddressForm.get('longitude').setValue(null, { emitEvent: false });
  }

  private setThirdPartyDeliveryAddress(propertyChanged: string) {
    let address = this.thirdPartyDeliveryAddress();
    if (address == null) {
      this.thirdPartyDeliveryAddress.set({
        id: null,
        isActive: true,
        name: null,
        email: null,
        phone: null,
        mobilePhone: null,
        street: this.form.get('deliveryAddress').get('street').value,
        address2: this.form.get('deliveryAddress').get('address2').value,
        longitude: this.form.get('deliveryAddress').get('longitude').value,
        latitude: this.form.get('deliveryAddress').get('latitude').value,
        note: null,
        openingTime: null,
        countryId: this.form.get('deliveryAddress').get('countryId').value,
        country: null,
        localityId: this.form.get('deliveryAddress').get('localityId').value,
        locality: null,
        roles: [],
        addressFrom: this.entity.deliveryAddress_AddressFrom,
      } as AddressDto);
    } else {
      address[propertyChanged] = this.form.get('deliveryAddress').get(propertyChanged).value;
      this.thirdPartyDeliveryAddress.set(structuredClone(address));
    }
  }

  protected onWorkingSiteChanged(workingSite: WorkingSiteLookUpDto) {
    this.updateThirdPartyDeliveryAddresses(workingSite, this.workingSites$);
  }

  protected onPointOfSaleChanged(pointOfSale: PointOfSaleLookupDto) {
    this.updateThirdPartyDeliveryAddresses(pointOfSale, this.pointOfSales$);
  }

  private updateThirdPartyDeliveryAddresses(
    element: { address: AddressDto },
    elements$: Observable<{ address: AddressDto }[]>,
  ) {
    if (element != null) {
      this.thirdPartyDeliveryAddresses$.next([
        ...this.thirdPartyDeliveryAddresses$.value,
        element.address,
      ]);
    } else {
      const addresses = this.thirdPartyDeliveryAddresses$.value;
      elements$.subscribe(x => {
        const allElements = x;
        const elementAddressToRemove = addresses.find(address =>
          allElements.map(x => x.address).find(x => x.name == address.name),
        );

        this.thirdPartyDeliveryAddresses$.next(
          addresses.filter(x => x.name != elementAddressToRemove?.name),
        );

        if (this.form.get('deliveryAddress').get('name').value == elementAddressToRemove?.name) {
          this.form.get('deliveryAddress').get('name').setValue(null);
          this.form.get('deliveryAddress').get('street').setValue(null);
          this.form.get('deliveryAddress').get('address2').setValue(null);
          this.form.get('deliveryAddress').get('countryId').setValue(null);
          this.form.get('deliveryAddress').get('localityId').setValue(null);
          this.form.get('deliveryAddress').get('latitude').setValue(null);
          this.form.get('deliveryAddress').get('longitude').setValue(null);
        }
      });
    }
  }

  protected override async afterGetEntity(response: TDocDto) {
    await super.afterGetEntity(response);
    //do not delete, necessary to trigger valuechanges on docOriginId in DocumentCustomerStatusCardComponent
    if (this.entity.deliveryAddress_Name != null || this.entity.deliveryAddress_Address1 != null) {
      this.thirdPartyDeliveryAddress.set({
        id: this.entity.deliveryAddress_ThirdPartyAddressId,
        isActive: true,
        name: this.entity.deliveryAddress_Name,
        email: null,
        phone: null,
        mobilePhone: null,
        street: this.entity.deliveryAddress_Address1,
        address2: this.entity.deliveryAddress_Address2,
        longitude: this.entity.deliveryAddress_Latitude,
        latitude: this.entity.deliveryAddress_Longitude,
        note: null,
        openingTime: null,
        countryId: this.entity.deliveryAddress_CountryId,
        country: null,
        localityId: this.entity.deliveryAddress_LocalityId,
        locality: null,
        roles: [],
        addressFrom: this.entity.deliveryAddress_AddressFrom,
      } as AddressDto);
    } else {
      this.thirdPartyDeliveryAddress.set(null);
    }

    const pointOfSaleForm = this.form.get('pointOfSale').value;
    if (pointOfSaleForm) {
      this.pointOfSales$.pipe(takeUntil(this.destroy$)).subscribe(x => {
        const pointOfSale = x.find(pointOfSale => pointOfSale.name == pointOfSaleForm);
        this.updateThirdPartyDeliveryAddresses(pointOfSale, this.pointOfSales$);
      });
    }

    const workingSiteForm = this.form.get('workingSiteId').value;
    if (workingSiteForm) {
      this.workingSites$.pipe(takeUntil(this.destroy$)).subscribe(x => {
        const workingSite = x.find(workingSite => workingSite.id == workingSiteForm);
        this.updateThirdPartyDeliveryAddresses(workingSite, this.workingSites$);
      });
    }

    this.customerProjectId.set(response.customerProjectId);
  }

  protected loadThirdParties(isClosed: boolean) {
    this.thirdPartyId$
      .pipe(first())
      .pipe(distinctUntilChanged())
      .subscribeOnce(thirdPartyId => {
        if (thirdPartyId != null) {
          this.loadThirdPartyAddress(thirdPartyId);
        } else {
          this.customerAddress = null;
        }
      });
  }

  protected loadThirdPartyAddress(thirdPartyId: string) {
    this.thirdPartyService
      .getEntityAddress(thirdPartyId)
      .subscribeOnce(customerAddress => (this.customerAddress = customerAddress));
  }

  protected override disableForm() {
    super.disableForm();
    this.form.get('customerProjectId').enable({ emitEvent: false });
  }
}
